import { CdkDrag, CdkDragDrop, CdkDropList, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import type { CaseDataField } from '@protocols/domain';
import { Observable, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';

import { EDITOR_DIALOG_SMALL } from '../../editor-dialog-sizes';
import { StagedCaseDataState } from '../../../state/staged-case-data.state';
import {
  CaseDataFieldEditorDialogComponent,
  CaseDataFieldEditorDialogData,
  CaseDataFieldType,
} from '../case-data-field-editor-dialog/case-data-field-editor-dialog.component';

interface CaseDataFieldRow {
  field: CaseDataField;
  index: number;
  displayName: string;
  icon: string;
  canRemove: boolean;
  canReorder: boolean;
  encrypted: boolean;
}

/** Per-variant leading icon (kept from the old `field-types` list). */
const FIELD_ICONS: Record<CaseDataFieldType, string> = {
  case_name: 'ri-price-tag-3-fill',
  label: 'ri-price-tag-3-fill',
  boolean: 'ri-check-line',
  date: 'ri-calendar-2-line',
  textedit: 'ri-pencil-line',
  number: 'ri-hashtag',
  switcher: 'ri-list-unordered',
  selection: 'ri-list-unordered',
  location: 'ri-focus-2-line',
  sectionHeader: 'ri-heading',
};

/**
 * Smart manager for the case type's data fields. Looks like
 * `CategoryManagementListComponent` (title + description + reorderable list +
 * per-row edit/delete + bottom "Add Field" button), with a per-field-type icon
 * before each display name. Staging lives entirely in the consumer-provided
 * {@link StagedCaseDataState}; this component writes into it directly (no
 * passthrough facade — there are no effects/use-cases) and opens the dumb
 * {@link CaseDataFieldEditorDialogComponent} for create/edit.
 *
 * Delete uses the inline two-click confirm pattern (category-management-list
 * parity) with a "Remove this field?" warning. The mandatory `case_name` field
 * cannot be removed (old-editor rule), and it is pinned to the top of the
 * list: it cannot be dragged, and no other field may be dropped above it.
 */
@Component({
  selector: 'ts-case-data-manager',
  templateUrl: './case-data-manager.component.html',
  styleUrls: ['./case-data-manager.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, DragDropModule, MatDialogModule, MatTooltipModule],
})
export class CaseDataManagerComponent implements OnDestroy {
  public readonly rows$: Observable<CaseDataFieldRow[]> = this.state.fields$.pipe(
    map((fields) => fields.map((field, index) => this.toRow(field, index)))
  );

  public pendingRemovalIndex: number | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly state: StagedCaseDataState, private readonly dialog: MatDialog) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public onAdd(): void {
    // A brand-new field is never persisted yet, so its internal name is free to
    // track the display name.
    this.openDialog(null, false).subscribe((field) => {
      if (field) this.state.addField(field);
    });
  }

  public onEdit(row: CaseDataFieldRow): void {
    // Freeze the internal name only if this field belongs to a persisted case
    // type (its name is in the locked ledger); a session-added field stays free.
    this.state.lockedNames$
      .pipe(
        take(1),
        map((lockedNames) => lockedNames.includes(row.field.name)),
        switchMap((lockName) => this.openDialog(row.field, lockName)),
        takeUntil(this.destroy$)
      )
      .subscribe((field) => {
        if (field) this.state.updateField({ index: row.index, field });
      });
  }

  public requestRemove(row: CaseDataFieldRow): void {
    if (!row.canRemove) return;
    this.pendingRemovalIndex = row.index;
  }

  public confirmRemove(row: CaseDataFieldRow): void {
    this.pendingRemovalIndex = null;
    this.state.removeField(row.index);
  }

  public cancelRemove(): void {
    this.pendingRemovalIndex = null;
  }

  public onDrop(rows: CaseDataFieldRow[], event: CdkDragDrop<CaseDataFieldRow[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    if (!rows[event.previousIndex]?.canReorder || event.currentIndex < this.minDropIndex(rows)) return;
    const fields = rows.map((row) => row.field);
    moveItemInArray(fields, event.previousIndex, event.currentIndex);
    this.state.reorder(fields);
  }

  /** Keeps the pinned case-name row on top: no dragged row may land on or above it. */
  public readonly sortPredicate = (index: number, _drag: CdkDrag, drop: CdkDropList<CaseDataFieldRow[]>): boolean =>
    index >= this.minDropIndex(drop.data ?? []);

  private openDialog(field: CaseDataField | null, lockName: boolean): Observable<CaseDataField | undefined> {
    return this.dialog
      .open<CaseDataFieldEditorDialogComponent, CaseDataFieldEditorDialogData, CaseDataField | undefined>(
        CaseDataFieldEditorDialogComponent,
        {
          ...EDITOR_DIALOG_SMALL,
          hasBackdrop: true,
          data: { field, lockName },
        }
      )
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$));
  }

  /** First index a dragged row may occupy — the slot right below the pinned rows. */
  private minDropIndex(rows: CaseDataFieldRow[]): number {
    const firstMovable = rows.findIndex((row) => row.canReorder);
    return firstMovable === -1 ? rows.length : firstMovable;
  }

  private toRow(field: CaseDataField, index: number): CaseDataFieldRow {
    return {
      field,
      index,
      displayName: field.displayName,
      icon: FIELD_ICONS[field.type] ?? 'ri-file-list-line',
      canRemove: field.type !== 'case_name',
      canReorder: field.type !== 'case_name',
      encrypted: field.encrypted,
    };
  }
}
